import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  InputDiv,
  Card,
  Select,
  Result,
  MathDiv,
  ActionButtonsRow,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function SteelCompressive() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fy, setFy] = useState(345)
  const [e, setE] = useState(200000)
  const [kxlx, setKxlx] = useState(300)
  const [kyly, setKyly] = useState(300)
  const [kzlz, setKzlz] = useState(300)
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [profileName, setProfileName] = useState('W150X13,0')
  const [profiles, setProfiles] = useState([])
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  useEffect(() => {
    async function fetchProfiles() {
      const response = await api.get('/wprofiles')
      setProfiles([...response.data.profiles])
    }

    fetchProfiles()
  }, [])

  const handleProfileChange = (event) => {
    const selectedProfile = event.target.value

    setProfileName(selectedProfile)
  }

  const HISTORY_KEY = 'steel-compressive-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFy(entry.fy)
      setE(entry.e)
      setProfileName(entry.profileName)
      setKxlx(entry.kxlx)
      setKyly(entry.kyly)
      setKzlz(entry.kzlz)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'rhoMin' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fy = validateField('fy', fy)
    newErrors.e = validateField('e', e)
    newErrors.kxlx = validateField('kxlx', kxlx)
    newErrors.kyly = validateField('kyly', kyly)
    newErrors.kzlz = validateField('kzlz', kzlz)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fy, e, kxlx, kyly, kzlz, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        fy,
        e,
        profileName,
        kxlx,
        kyly,
        kzlz,
      }

      const response = await api.post('/steel/compressive', data)

      setData(data)

      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fy,
        e,
        profileName,
        kxlx,
        kyly,
        kzlz,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fy,
      e,
      profileName,
      kxlx,
      kyly,
      kzlz,
      data,
      result,
      isFormValid,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Aço compressão.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFy(jsonData.fy)
        setE(jsonData.e)
        setProfileName(jsonData.profileName)
        setKxlx(jsonData.kxlx)
        setKyly(jsonData.kyly)
        setKzlz(jsonData.kzlz)

        setResult(jsonData.result)
        setData(jsonData.data)
        setIsFormValid(jsonData.isFormValid)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Compressão</span>
          </div>
          <h1>🔩 Compressão em Perfis de Aço</h1>
          <p className="subtitle">Dimensionamento e verificação de perfis de aço submetidos à compressão axial</p>
          <div className="badges">
            <Badge>📋 ABNT NBR 8800:2008</Badge>
            <Badge>🧮 Flambagem e Instabilidade</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de perfis de aço submetidos à compressão axial, conforme a ABNT
              NBR 8800:2008.
            </p>
            <p>
              O cálculo considera os efeitos de flambagem local da alma (FLA) e da mesa (FLM), além da flambagem global
              por flexão e flexo-torção, determinando a carga resistente de compressão do perfil selecionado.
            </p>
            <div className="norms">
              <span>ABNT NBR 8800:2008</span>
              <span>Perfis Laminados</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <h3>Materiais</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Tensão de escoamento do perfil (MPa)</span>
              <ValidatedInput
                name="fy"
                type="number"
                value={fy || ''}
                onChange={(event) => setFy(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Módulo de elasticidade (MPa)</span>
              <ValidatedInput
                name="e"
                type="number"
                value={e || ''}
                onChange={(event) => setE(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            {profiles.length > 0 && (
              <InputDiv>
                <span>Perfil</span>
                <div>
                  <Select
                    style={{ marginLeft: '0' }}
                    id="profileSelect"
                    value={profileName}
                    onChange={handleProfileChange}
                  >
                    {profiles.map((profile, index) => (
                      <option key={index} value={profile.name}>
                        {profile.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </InputDiv>
            )}
            <InputDiv>
              <span>KxLx (cm)</span>
              <ValidatedInput
                name="kxlx"
                type="number"
                value={kxlx || ''}
                onChange={(event) => setKxlx(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>KyLy (cm)</span>
              <ValidatedInput
                name="kyly"
                type="number"
                value={kyly || ''}
                onChange={(event) => setKyly(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>KzLz (cm)</span>
              <ValidatedInput
                name="kzlz"
                type="number"
                value={kzlz || ''}
                onChange={(event) => setKzlz(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <h3>Verificação de FLA</h3>
                <BlockMath math={`q_a = ${result.fla.q.toFixed(2)}`} />
                <h3>Verificação de FLM</h3>
                <BlockMath math={`q_s = ${result.flm.q.toFixed(2)}`} />
                <h3>Carga de flambagem elástica</h3>
                <BlockMath math={`N_{e} = ${result.ne.toFixed(2)} \\text{ kN}`} />
                <h3>Carga resistente de compressão</h3>
                <BlockMath math={`N_{c,Rd} =  ${result.ncrd.toFixed(2)} \\text{ kN}`} />
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <MathDiv>
                <h3>Verificação de FLA</h3>
                <BlockMath
                  math={`\\lambda = \\dfrac{b}{t} = \\dfrac{${result.profile.dl}}{${result.profile.tw}} = ${result.fla.lambda.toFixed(2)}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\lambda_p = 1.49 \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} = 1.49 \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} = ${result.fla.lambdaP.toFixed(2)}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`\\lambda_p = 1.49 \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}}`} />
                  <BlockMath
                    math={`\\lambda_p = 1.49 \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} = ${result.fla.lambdaP.toFixed(2)}`}
                  />
                </MobileOnlyDiv>
                {result.fla.case == 1 ? (
                  <>
                    <BlockMath math={`\\lambda \\leq \\lambda_p \\therefore \\boxed{q_a = 1}`} />
                  </>
                ) : (
                  <>
                    <BlockMath math={`\\lambda > \\lambda_p`} />
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`b_{ef} = 1.92 \\cdot t \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} \\cdot \\left(1 - \\dfrac{c_a}{b/t} \\right) \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} \\leq b`}
                      />
                      <BlockMath
                        math={`b_{ef} = 1.92 \\cdot ${result.profile.tw} \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} \\cdot \\left(1 - \\dfrac{${result.fla.ca}}{${result.profile.dl} / ${result.profile.tw}} \\right) \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} \\leq ${result.profile.dl}`}
                      />
                      <BlockMath math={`b_{ef} = ${result.fla.beff.toFixed(2)} \\text{ cm}`} />
                      <BlockMath
                        math={`A_f = b_{ef} \\cdot t_w + 2 \\cdot b_f \\cdot t_f = ${result.fla.beff.toFixed(2)} \\cdot ${result.profile.tw} + 2 \\cdot ${result.profile.bf} \\cdot ${result.profile.tf} = ${result.fla.af.toFixed(2)} \\text{ cm}^2`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath
                        math={`b_{ef} = 1.92 \\cdot t \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} \\cdots`}
                      />
                      <BlockMath
                        math={`\\left(1 - \\dfrac{c_a}{b/t} \\right) \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} \\leq b`}
                      />
                      <BlockMath
                        math={`b_{ef} = 1.92 \\cdot ${result.profile.tw} \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} \\cdots`}
                      />
                      <BlockMath
                        math={`\\left(1 - \\dfrac{${result.fla.ca}}{${result.profile.dl} / ${result.profile.tw}} \\right) \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} \\leq ${result.profile.dl}`}
                      />
                      <BlockMath math={`b_{ef} = ${result.fla.beff.toFixed(2)} \\text{ cm}`} />
                      <BlockMath math={`A_f = b_{ef} \\cdot t_w + 2 \\cdot b_f \\cdot t_f`} />
                      <BlockMath
                        math={`A_f = ${result.fla.beff.toFixed(2)} \\cdot ${result.profile.tw} + 2 \\cdot ${result.profile.bf} \\cdot ${result.profile.tf}`}
                      />
                      <BlockMath math={`A_f = ${result.fla.af.toFixed(2)} \\text{ cm}^2`} />
                    </MobileOnlyDiv>
                    <BlockMath
                      math={`\\boxed{q_a = \\dfrac{A_f}{A_g} = \\dfrac{${result.fla.af.toFixed(2)}}{${result.profile.A.toFixed(2)}} = ${result.fla.q.toFixed(2)}}`}
                    />
                  </>
                )}
                <h3>Verificação de FLM</h3>
                <BlockMath
                  math={`\\lambda = \\dfrac{b}{t} = \\dfrac{${result.profile.bf / 2}}{${result.profile.tf}} = ${result.flm.lambda.toFixed(2)}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\lambda_p = 0.56 \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} = 0.56 \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} = ${result.flm.lambdaP.toFixed(2)}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`\\lambda_p = 0.56 \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}}`} />
                  <BlockMath
                    math={`\\lambda_p = 0.56 \\cdot \\left(\\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} = ${result.flm.lambdaP.toFixed(2)}`}
                  />
                </MobileOnlyDiv>
                {result.flm.case == 1 && (
                  <>
                    <BlockMath
                      math={`\\boxed{\\lambda \\leq 0.56 \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} \\therefore q_s = 1}`}
                    />
                  </>
                )}
                {result.flm.case == 2 && (
                  <>
                    <BlockMath
                      math={`0.56 \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}} < \\lambda \\leq 1.03 \\cdot \\left(\\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}}`}
                    />
                    <BlockMath
                      math={`\\boxed{q_s = 1.415 - 0.74 \\cdot \\lambda \\cdot \\left(\\dfrac{f_y}{E} \\right)^{\\dfrac{1}{2}} = 1.415 - 0.74 \\cdot ${result.flm.lambda.toFixed(2)} \\cdot \\left(\\dfrac{${data.fy}}{${data.e}} \\right)^{\\dfrac{1}{2}} = ${result.flm.q.toFixed(2)}}`}
                    />
                  </>
                )}
                <h3>Carga de flambagem elástica</h3>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`N_{ex} = \\dfrac{\\pi^2 \\cdot E \\cdot I_x}{\\left(K_x \\cdot L_x \\right)^2} = \\dfrac{\\pi^2 \\cdot ${data.e / 10} \\cdot ${result.profile.ix}}{\\left(${data.kxlx} \\right)^2} = ${result.nex.toFixed(2)} \\text{ kN}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`N_{ex} = \\dfrac{\\pi^2 \\cdot E \\cdot I_x}{\\left(K_x \\cdot L_x \\right)^2} = \\dfrac{\\pi^2 \\cdot ${data.e / 10} \\cdot ${result.profile.ix}}{\\left(${data.kxlx} \\right)^2}`}
                  />
                  <BlockMath math={`N_{ex} = ${result.nex.toFixed(2)} \\text{ kN}`} />
                </MobileOnlyDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`N_{ey} = \\dfrac{\\pi^2 \\cdot E \\cdot I_y}{\\left(K_y \\cdot L_y \\right)^2} = \\dfrac{\\pi^2 \\cdot ${data.e / 10} \\cdot ${result.profile.iy}}{\\left(${data.kyly} \\right)^2} = ${result.ney.toFixed(2)} \\text{ kN}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`N_{ey} = \\dfrac{\\pi^2 \\cdot E \\cdot I_y}{\\left(K_y \\cdot L_y \\right)^2} = \\dfrac{\\pi^2 \\cdot ${data.e / 10} \\cdot ${result.profile.iy}}{\\left(${data.kyly} \\right)^2}`}
                  />
                  <BlockMath math={`N_{ey} = ${result.ney.toFixed(2)} \\text{ kN}`} />
                </MobileOnlyDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`r_0 = \\left({r_x}^2 + {r_y}^2 \\right)^{\\frac{1}{2}} = \\left({${result.profile.rx}}^2 + {${result.profile.ry}}^2 \\right)^{\\frac{1}{2}} = ${result.r0.toFixed(2)} \\text{ cm}`}
                  />
                  <BlockMath
                    math={`N_{ez} = \\dfrac{1}{{r_0}^2} \\cdot \\left[ \\dfrac{\\pi^2 \\cdot E \\cdot C_w}{\\left(K_z \\cdot L_z \\right)^2} + G \\cdot I_t \\right] = \\dfrac{1}{{${result.r0.toFixed(2)}}^2} \\cdot \\left[ \\dfrac{\\pi^2 \\cdot ${data.e / 10} \\cdot ${result.profile.cw} }{\\left(${data.kzlz} \\right)^2} + ${result.g.toFixed(0)} \\cdot ${result.profile.it} \\right] = ${result.nez.toFixed(2)} \\text{ kN}`}
                  />
                  <BlockMath
                    math={`\\boxed{N_{e} = \\text{mín} \\left( N_{ex}, N_{ey}, N_{ez} \\right) = ${result.ne.toFixed(2)} \\text{ kN}}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`r_0 = \\left({r_x}^2 + {r_y}^2 \\right)^{\\frac{1}{2}} = \\left({${result.profile.rx}}^2 + {${result.profile.ry}}^2 \\right)^{\\frac{1}{2}}`}
                  />
                  <BlockMath math={`r_0 = ${result.r0.toFixed(2)} \\text{ cm}`} />
                  <BlockMath
                    math={`N_{ez} = \\dfrac{1}{{r_0}^2} \\cdot \\left[ \\dfrac{\\pi^2 \\cdot E \\cdot C_w}{\\left(K_z \\cdot L_z \\right)^2} + G \\cdot I_t \\right]`}
                  />
                  <BlockMath math={`N_{ez} = \\dfrac{1}{{${result.r0.toFixed(2)}}^2} \\cdots`} />
                  <BlockMath
                    math={`\\cdots \\left[ \\dfrac{\\pi^2 \\cdot ${data.e / 10} \\cdot ${result.profile.cw} }{\\left(${data.kzlz} \\right)^2} + ${result.g.toFixed(0)} \\cdot ${result.profile.it} \\right]`}
                  />
                  <BlockMath math={`N_{ez} = ${result.nez.toFixed(2)} \\text{ kN}`} />
                  <BlockMath
                    math={`\\boxed{N_{e} = \\text{mín} \\left( N_{ex}, N_{ey}, N_{ez} \\right) = ${result.ne.toFixed(2)} \\text{ kN}}`}
                  />
                </MobileOnlyDiv>
                <h3>Carga resistente de compressão</h3>
                <BlockMath
                  math={`q = q_s \\cdot q_a = ${result.flm.q.toFixed(2)} \\cdot ${result.fla.q.toFixed(2)} = ${result.q.toFixed(2)}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\lambda_0 = \\left( \\dfrac{Q \\cdot A_g \\cdot f_y}{N_e} \\right)^{\\frac{1}{2}} = \\left( \\dfrac{${result.q.toFixed(2)} \\cdot ${result.profile.A} \\cdot ${data.fy}}{${result.ne.toFixed(2)}} \\right)^{\\frac{1}{2}} = ${result.lambda0.toFixed(2)}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`\\lambda_0 = \\left( \\dfrac{Q \\cdot A_g \\cdot f_y}{N_e} \\right)^{\\frac{1}{2}}`}
                  />
                  <BlockMath
                    math={`\\lambda_0 = \\left( \\dfrac{${result.q.toFixed(2)} \\cdot ${result.profile.A} \\cdot ${data.fy}}{${result.ne.toFixed(2)}} \\right)^{\\frac{1}{2}} = ${result.lambda0.toFixed(2)}`}
                  />
                  <BlockMath math={`\\lambda_0 = ${result.lambda0.toFixed(2)}`} />
                </MobileOnlyDiv>
                {result.chi.case == 1 && (
                  <>
                    <BlockMath
                      math={`\\lambda_0 \\leq 1.5 \\therefore \\chi = 0.658^{{\\lambda_0}^2} = ${result.chi.value.toFixed(2)}`}
                    />
                  </>
                )}
                {result.chi.case == 2 && (
                  <>
                    <BlockMath
                      math={`\\lambda_0 > 1.5 \\therefore \\chi = \\dfrac{0.877}{{\\lambda_0}^2} = ${result.chi.value.toFixed(2)}`}
                    />
                  </>
                )}
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`\\boxed{N_{c,Rd} = \\dfrac{\\chi \\cdot Q \\cdot A_g \\cdot f_y}{\\gamma_{a1}} = \\dfrac{${result.chi.value.toFixed(2)} \\cdot ${result.q.toFixed(2)} \\cdot ${result.profile.A} \\cdot ${data.fy}}{1.1} = ${result.ncrd.toFixed(2)} \\text{ kN}}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`N_{c,Rd} = \\dfrac{\\chi \\cdot Q \\cdot A_g \\cdot f_y}{\\gamma_{a1}}`} />
                  <BlockMath
                    math={`N_{c,Rd} = \\dfrac{${result.chi.value.toFixed(2)} \\cdot ${result.q.toFixed(2)} \\cdot ${result.profile.A} \\cdot ${data.fy}}{1.1}`}
                  />
                  <BlockMath math={`\\boxed{N_{c,Rd} = ${result.ncrd.toFixed(2)} \\text{ kN}}`} />
                </MobileOnlyDiv>
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
